import qs from 'qs';
import {
  CustomerBillingAddressContract,
  CustomerCreateContract,
  CustomerDataContract,
  CustomerLoginContract,
  CustomerPaymentProfileContract,
  CustomerShippingAddressContract,
  ICustomerService,
} from '../../types/Customer';
import { CustomerServiceConfig } from '../../types/ServiceConfigurations';
import ServiceError from '../../models/classes/ServiceError';
import {
  CredentialedParameters,
  CredentialedPostParameters,
  CredentialedPutParameters,
  LoginPostParameters,
} from '../RequestParameters';
import ApiValidationError from '../../models/classes/ApiValidationError';
import { ConfigEnabledPaymentMethodContract } from '../../types/Configuration';
import { Endpoints } from '../../utils/constants';

const ApiError = new ServiceError('CustomerService', 'error contacting API');
const NotLoggedInError = new ServiceError(
  'CustomerService',
  'customer is not logged in'
);
const NewCustomerError = new ServiceError(
  'CustomerService',
  'failed to create customer'
);
const NoDataError = new ServiceError(
  'CustomerService',
  'no cart data returned'
);

class CustomerService implements ICustomerService {
  private storeUrl: string;

  constructor(config: CustomerServiceConfig) {
    this.storeUrl = config.apiUrl;
  }

  public async getCustomerDataAsync(): Promise<CustomerDataContract> {
    try {
      const response = await window.fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        if (!responseJson?.data?.id) {
          throw new Error('invalid customer id returned');
        }
        return responseJson.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }

    throw NotLoggedInError;
  }

  public async postNewCustomerDataAsync(
    newCustomer: CustomerCreateContract
  ): Promise<number> {
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.NEW_CUSTOMER}`,
        {
          ...CredentialedPostParameters,
          body: JSON.stringify(newCustomer),
        }
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.data.id) {
          return responseJson.data.id;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw NewCustomerError;
    }
    throw NewCustomerError;
  }

  public async getShippingAddressListAsync(): Promise<
    Array<CustomerShippingAddressContract>
  > {
    try {
      const response = await window.fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_SHIPPING_ADDRESSES}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        return responseJson.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async postShippingAddressDataAsync(
    shippingAddress: CustomerShippingAddressContract
  ): Promise<CustomerShippingAddressContract> {
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_SHIPPING_ADDRESSES}?validatephone=false`,
        {
          ...CredentialedPostParameters,
          body: JSON.stringify(shippingAddress),
        }
      );
      const { data } = await response.json();
      if (response.status === 200)
        return data as CustomerShippingAddressContract;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async putShippingAddressDataAsync(
    shippingAddress: CustomerShippingAddressContract
  ): Promise<CustomerShippingAddressContract> {
    if (!shippingAddress?.id) {
      throw TypeError('Shipping Address should have id.');
    }
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_SHIPPING_ADDRESSES}/${shippingAddress.id}?validatephone=false`,
        {
          ...CredentialedPutParameters,
          body: JSON.stringify(shippingAddress),
        }
      );
      const { data } = await response.json();
      if (response.status === 200)
        return data as CustomerShippingAddressContract;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async getBillingAddressListAsync(): Promise<
    Array<CustomerBillingAddressContract>
  > {
    try {
      const response = await window.fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_BILLING_ADDRESSES}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        return responseJson.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async postBillingAddressDataAsync(
    billingAddress: CustomerBillingAddressContract
  ): Promise<CustomerBillingAddressContract> {
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_BILLING_ADDRESSES}?validatephone=false`,
        {
          ...CredentialedPostParameters,
          body: JSON.stringify(billingAddress),
        }
      );
      const billingAddressResponse = await response.json();
      if (response.status === 200)
        return billingAddressResponse.data as CustomerBillingAddressContract;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async getPaymentProfileListAsync(): Promise<
    Array<CustomerPaymentProfileContract>
  > {
    try {
      const response = await window.fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_PAYMENT_PROFILES}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        return responseJson.data;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async postPaymentProfileDataAsync(
    paymentProfile: CustomerPaymentProfileContract
  ): Promise<void> {
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_PAYMENT_PROFILES}?validatephone=false`,
        {
          ...CredentialedPostParameters,
          body: JSON.stringify(paymentProfile),
        }
      );
      if (response.status === 200) return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw NotLoggedInError;
  }

  public async putPaymentProfileDataAsync(
    paymentProfile: CustomerPaymentProfileContract
  ): Promise<void> {
    if (!paymentProfile?.id) {
      return;
    }
    try {
      const response = await fetch(
        `${this.storeUrl}${Endpoints.CURRENT_CUSTOMER_PAYMENT_PROFILES}/${paymentProfile.id}`,
        {
          ...CredentialedPutParameters,
          body: JSON.stringify(paymentProfile),
        }
      );
      if (response.status === 200) return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
    throw ApiError;
  }

  public async isEmailRegisteredAsync(email: string): Promise<boolean> {
    try {
      const response = await window.fetch(
        `${this.storeUrl}/api/v1/users?email=${encodeURIComponent(email)}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        return true;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return false;
    }
    return false;
  }

  public async loginCustomerAsync(login: CustomerLoginContract): Promise<void> {
    try {
      const response = await fetch(`${this.storeUrl}${Endpoints.LOGIN}`, {
        ...LoginPostParameters,
        body: qs.stringify({
          email: login.emailAddress,
          password: login.password,
          'g-recaptcha-response': login.reCaptchaValue,
          CustomerNewOld: 'old',
        }),
      });
      const responseJson = await response.json();
      if (responseJson?.Errors?.length) {
        throw new ApiValidationError(
          'Customer login failed',
          responseJson.Errors
        );
      }

      window?.dtrum?.identifyUser?.(login.emailAddress);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.isValidationError) {
        throw error;
      }
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }

  public async logoutCustomerAsync(): Promise<void> {
    try {
      await fetch(
        `${this.storeUrl}/login.asp?logout=yes`,
        CredentialedParameters
      );
    } catch (error) {
      // TODO: Handle thrown error
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  public async getCustomerEnabledPaymentMethods(): Promise<ConfigEnabledPaymentMethodContract> {
    try {
      const response = await window.fetch(
        `${this.storeUrl}${Endpoints.ENABLED_PAYMENT_METHODS}`,
        CredentialedParameters
      );
      if (response.status === 200) {
        const responseJson = await response.json();
        return responseJson.data.enabledPaymentMethods;
      }
      throw NoDataError;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw ApiError;
    }
  }
}

export default CustomerService;
